import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { Hole, HoleYardage, Round } from '../../../models';
import { FormsModule } from '@angular/forms';

import { ConfirmationDialogComponent, ConfirmationDialogOptions } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { DialogService } from '../../../services/shared/dialog/dialog.service';

@Component({
  selector: 'app-hole-info',
  standalone: true,
  imports: [FormsModule, ConfirmationDialogComponent],
  templateUrl: './hole-info.component.html',
  styleUrl: './hole-info.component.scss',
})
export class HoleInfoComponent {
  @ViewChild('copyYardagesDialog') copyYardagesDialog?: ConfirmationDialogComponent;
  @ViewChildren('parInput', { read: ElementRef }) parInputs?: QueryList<ElementRef<HTMLInputElement>>;

  private pendingCopyContext: CopyYardageContext | null = null;
  private readonly allowedParValues = new Set<number>([3, 4, 5, 6, 7]);
  private readonly allowedParKeys = new Set(['3', '4', '5', '6', '7']);
  private readonly cachedParValues = new Map<string, number | undefined>();

  @Output()
  holeChanged = new EventEmitter<Hole>();

  @Output()
  holeYardageChanged = new EventEmitter<HoleYardage>();

  @Input()
  readOnly: boolean = false;

  private _rounds: Round[] = [];
  @Input()
  get rounds(): Round[] {
    return this._rounds;
  }
  set rounds(value: Round[]) {
    this._rounds = value;
    if (this._rounds.length > 0) {
      this.selectedRound = this._rounds[0];
    }
  }

  private _selectedRound: Round = {} as Round;
  get selectedRound(): Round {
    return this._selectedRound;
  }
  set selectedRound(value: Round) {
    this._selectedRound = value;
    this.filterHoleYardagesByRound();
  }

  private _holes: Hole[] = [];
  @Input()
  get holes(): Hole[] {
    return this._holes;
  }
  set holes(value: Hole[]) {
    this._holes = value;
    this.holesOUT = value.filter((hole) => hole.number! <= 9);
    this.holesIN = value.filter((hole) => hole.number! > 9);
    this.rebuildCachedParValues(value);
  }

  holesIN: Hole[] = [];
  holesOUT: Hole[] = [];

  private _holeYardages: HoleYardage[] = [];
  @Input()
  get holeYardages(): HoleYardage[] {
    return this._holeYardages;
  }
  set holeYardages(value: HoleYardage[]) {
    this._holeYardages = value;
    this.filterHoleYardagesByRound();
  }

  holeYardagesOUT: HoleYardage[] = [];
  holeYardagesIN: HoleYardage[] = [];

  get parTotalIN(): number {
    return this.holesIN.reduce(
      (total: number, hole: Hole) => total + (Number(hole.par) || 0),
      0
    );
  }
  get parTotalOUT(): number {
    return this.holesOUT.reduce(
      (total: number, hole: Hole) => total + (Number(hole.par) || 0),
      0
    );
  }
  get parTotal(): number {
    return this.parTotalIN + this.parTotalOUT;
  }

  get yardageTotalIN(): number {
    return this.holeYardagesIN.reduce(
      (total: number, hole: HoleYardage) => total + (Number(hole.yardage) || 0),
      0
    );
  }
  get yardageTotalOUT(): number {
    return this.holeYardagesOUT.reduce(
      (total: number, hole: HoleYardage) => total + (Number(hole.yardage) || 0),
      0
    );
  }
  get yardageTotal(): number {
    return this.yardageTotalOUT + this.yardageTotalIN;
  }

  constructor(private readonly dialogService: DialogService) { }

  numberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  onParModelChange(rawValue: string | number | null, hole: Hole, inputIndex: number): void {
    if (this.readOnly) {
      return;
    }

    const valueAsString = typeof rawValue === 'number'
      ? rawValue.toString()
      : (rawValue ?? '').toString();

    const trimmed = valueAsString.trim();
    const cacheKey = this.getHoleParCacheKey(hole);

    if (!trimmed) {
      hole.par = undefined;
      this.cachedParValues.set(cacheKey, undefined);
      this.setHoleChanged(hole);
      return;
    }

    if (trimmed.length > 1) {
      this.revertParInput(hole, inputIndex);
      return;
    }

    const numericValue = Number.parseInt(trimmed, 10);
    if (!this.allowedParValues.has(numericValue)) {
      this.revertParInput(hole, inputIndex);
      return;
    }

    hole.par = numericValue;
    this.cachedParValues.set(cacheKey, numericValue);
    this.setHoleChanged(hole);

    const inputs = this.parInputs?.toArray() ?? [];
    const currentInput = inputs[inputIndex]?.nativeElement;
    if (!currentInput || document.activeElement !== currentInput) {
      return;
    }

    this.focusParInputAt(inputIndex + 1);
  }

  onParKeyDown(event: KeyboardEvent): void {
    if (this.readOnly) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const key = event.key;

    if (key === 'Decimal') {
      event.preventDefault();
      return;
    }

    if (key.length > 1) {
      return;
    }

    if (!this.allowedParKeys.has(key)) {
      event.preventDefault();
    }
  }

  setHoleChanged(h: Hole): void {
    const cacheKey = this.getHoleParCacheKey(h);
    this.cachedParValues.set(cacheKey, this.normalizeParValue(h.par));
    this.holeChanged.emit(h);
  }

  setChangedHoleYardage(hy: HoleYardage): void {
    this.holeYardageChanged.emit(hy);
  }

  filterHoleYardagesByRound(): void {
    const roundId = this.selectedRound?.roundId;
    const yardagesForRound = this.holeYardages
      .filter((yardage) => yardage.roundId === roundId)
      .sort((a, b) => (a.holeNumber ?? 0) - (b.holeNumber ?? 0));

    this.holeYardagesOUT = yardagesForRound.filter((holeYardage) => (holeYardage.holeNumber ?? 0) <= 9);
    this.holeYardagesIN = yardagesForRound.filter((holeYardage) => (holeYardage.holeNumber ?? 0) > 9);
  }

  async selectRound(round: Round): Promise<void> {
    if (!round) {
      return;
    }

    const previousRound = this.findPreviousRound(round);
    const shouldPrompt = !!previousRound && this.isRoundMissingYardages(round);

    this.selectedRound = round;

    if (!shouldPrompt || !previousRound) {
      return;
    }

    const context: CopyYardageContext = {
      targetRound: round,
      sourceRound: previousRound,
    };

    this.pendingCopyContext = context;

    if (this.copyYardagesDialog) {
      const dialogOptions: ConfirmationDialogOptions = {
        title: 'Copy Yardages?',
        message: 'Would you like to copy previous round yardages to this round?',
        confirmButtonText: 'Copy Yardages',
        cancelButtonText: 'No',
        confirmButtonClass: 'btn-primary',
        data: context,
      };

      this.copyYardagesDialog.open(dialogOptions);
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Would you like to copy previous round yardages to this round?',
      {
        title: 'Copy Yardages?',
        confirmLabel: 'Copy Yardages',
        cancelLabel: 'No'
      }
    );

    if (confirmed) {
      this.applyYardageCopy(context);
    }

    this.pendingCopyContext = null;
  }

  onCopyYardagesConfirmed(context?: CopyYardageContext): void {
    const copyContext = context ?? this.pendingCopyContext ?? undefined;
    if (!copyContext) {
      this.pendingCopyContext = null;
      return;
    }

    this.applyYardageCopy(copyContext);
    this.pendingCopyContext = null;
  }

  onCopyYardagesCancelled(): void {
    this.pendingCopyContext = null;
  }

  private applyYardageCopy(context?: CopyYardageContext): void {
    if (!context) {
      return;
    }

    const { sourceRound, targetRound } = context;
    const sourceRoundId = sourceRound.roundId;
    const targetRoundId = targetRound.roundId;

    if (!sourceRoundId || !targetRoundId || !this.holes?.length) {
      return;
    }

    this.holes.forEach((hole) => {
      const sourceYardage = this.findYardageForRoundAndHole(sourceRoundId, hole);
      const targetYardage = this.findYardageForRoundAndHole(targetRoundId, hole);
      const copiedValue = this.normalizeYardageValue(sourceYardage?.yardage);

      if (targetYardage) {
        targetYardage.yardage = copiedValue;
        if (!targetYardage.teeboxId && sourceYardage?.teeboxId) {
          targetYardage.teeboxId = sourceYardage.teeboxId;
        }
        this.setChangedHoleYardage(targetYardage);
        return;
      }

      const newYardage: HoleYardage = {
        holeYardageId: undefined,
        holeId: hole.holeId,
        courseId: sourceYardage?.courseId ?? hole.courseId,
        teeboxId: sourceYardage?.teeboxId,
        roundId: targetRoundId,
        holeNumber: hole.number,
        yardage: copiedValue,
      };

      this.holeYardages.push(newYardage);
      this.setChangedHoleYardage(newYardage);
    });

    this.filterHoleYardagesByRound();
  }

  private findYardageForRoundAndHole(roundId: string, hole: Hole): HoleYardage | undefined {
    const holeId = hole.holeId;
    return this.holeYardages.find((yardage) => {
      if (yardage.roundId !== roundId) {
        return false;
      }
      if (holeId && yardage.holeId) {
        return yardage.holeId === holeId;
      }
      return (yardage.holeNumber ?? 0) === (hole.number ?? 0);
    });
  }

  private normalizeYardageValue(value: number | undefined | null): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  }

  private findPreviousRound(round: Round): Round | undefined {
    const currentNumber = round.number ?? 0;
    if (!currentNumber || currentNumber <= 1) {
      return undefined;
    }

    return this.rounds.find((candidate) => (candidate.number ?? 0) === currentNumber - 1);
  }

  private isRoundMissingYardages(round: Round): boolean {
    const roundId = round.roundId;
    if (!roundId) {
      return false;
    }

    const yardagesForRound = this.holeYardages.filter((yardage) => yardage.roundId === roundId);
    if (!yardagesForRound.length) {
      return true;
    }

    return !yardagesForRound.some((yardage) => this.hasPositiveYardage(yardage.yardage));
  }

  private hasPositiveYardage(value: number | undefined | null): boolean {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0;
  }

  private focusParInputAt(index: number): void {
    if (this.readOnly) {
      return;
    }

    const inputs = this.parInputs?.toArray() ?? [];
    if (index < 0 || index >= inputs.length) {
      return;
    }

    const nextInput = inputs[index]?.nativeElement;
    if (!nextInput) {
      return;
    }

    setTimeout(() => {
      nextInput.focus();
      nextInput.select();
    }, 0);
  }

  private revertParInput(hole: Hole, inputIndex: number): void {
    const cacheKey = this.getHoleParCacheKey(hole);
    const previousValue = this.cachedParValues.get(cacheKey);
    hole.par = previousValue;

    const inputs = this.parInputs?.toArray() ?? [];
    const inputElement = inputs[inputIndex]?.nativeElement;
    if (!inputElement) {
      return;
    }

    setTimeout(() => {
      inputElement.value = previousValue !== undefined && previousValue !== null
        ? previousValue.toString()
        : '';
      inputElement.select();
    }, 0);
  }

  private rebuildCachedParValues(holes: Hole[]): void {
    this.cachedParValues.clear();
    holes.forEach((hole) => {
      const cacheKey = this.getHoleParCacheKey(hole);
      const numericValue = this.normalizeParValue(hole.par);
      this.cachedParValues.set(cacheKey, numericValue);
    });
  }

  private normalizeParValue(value: unknown): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private getHoleParCacheKey(hole: Hole): string {
    const identifier = hole.holeId ?? `no-id-${hole.number ?? 'unknown'}`;
    return `${identifier}`;
  }
}

interface CopyYardageContext {
  targetRound: Round;
  sourceRound: Round;
}
